import { useEffect, useMemo, useState } from 'react'
import {
  subscribePlanProposals,
  type AiPlanProposal,
} from '../../../../services/aiAgentBridge'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export type UseProposalExecutionModuleParams = {
  workspaceId: string
  uid?: string | null
  canApprove?: boolean
  timeRange: AnalysisTimeRangeKey
  /** Bump to force resubscribe (mirrors AnalysisHub snapshotNonce). */
  refreshKey?: number
  now?: Date
}

export const useProposalExecutionModule = ({
  workspaceId,
  uid = null,
  canApprove = true,
  timeRange,
  refreshKey = 0,
  now,
}: UseProposalExecutionModuleParams) => {
  const [proposals, setProposals] = useState<AiPlanProposal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!workspaceId || !uid) {
      setProposals([])
      setLoading(false)
      return undefined
    }

    setLoading(true)
    const unsubscribe = subscribePlanProposals({
      workspaceId,
      uid,
      canApprove,
      onChange: (loaded) => {
        setProposals(loaded)
        setLoading(false)
      },
      onError: (err) => {
        setError(err)
        setProposals([])
        setLoading(false)
      },
    })

    return () => unsubscribe()
  }, [workspaceId, uid, canApprove, refreshKey])

  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    // Filter executed proposals in time range
    const executedInWindow = proposals.filter((p) => {
      if (p.status !== 'EXECUTED') return false
      const c = p.updatedAt || p.createdAt
      const millis =
        c instanceof Date
          ? c.getTime()
          : typeof c === 'number'
            ? c
            : c && typeof c === 'object' && 'toMillis' in c && typeof (c as { toMillis: () => number }).toMillis === 'function'
              ? (c as { toMillis: () => number }).toMillis()
              : 0
      return millis >= from.getTime() && millis <= to.getTime()
    })

    const timestamps = executedInWindow.map((p) => {
      const c = p.updatedAt || p.createdAt
      if (c instanceof Date) return c
      if (typeof c === 'number') return new Date(c)
      if (c && typeof c === 'object' && 'toDate' in c && typeof (c as { toDate: () => Date }).toDate === 'function') {
        return (c as { toDate: () => Date }).toDate()
      }
      return new Date()
    })

    const points = bucketize(timestamps, timeRange, now)
    const delta = calculateDelta(points)

    const records: LiveModuleRecord[] = executedInWindow
      .slice(0, 5)
      .map((p) => ({
        id: p.id,
        primary: p.title || 'AI Proposal',
        secondary: `${p.proposalType} · ${p.creditCost} credits`,
        numeric: p.creditCost,
        drillTarget: 'SUMMARY',
      }))

    const headline: LiveModuleHeadline = {
      value: executedInWindow.length,
      delta,
      label: 'executed',
    }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-good',
    }

    return {
      headline,
      sparkline,
      records,
      loading,
      error,
    }
  }, [proposals, timeRange, now, loading, error])
}
